import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ChevronDownIcon, MenuIcon, XIcon } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Wordmark } from '../brand/Wordmark';
import { NewTabHint } from '../ui/NewTabHint';
import { brandDescriptor } from '../../data/brandAssets';
import { externalLinks, primaryNav, allRoutes } from '../../data/site';
function desktopLinkClass({
  isActive
}: {
  isActive: boolean;
}) {
  return `relative py-2 font-display text-[15px] font-semibold text-brand-navy transition-colors hover:text-[#083a7c] ${isActive ? 'after:absolute after:-bottom-0.5 after:left-0 after:h-[3px] after:w-full after:rounded-full after:bg-brand-yellow after:content-[""]' : ''}`;
}
function ResourcesMenu({
  items
}: {
  items: {
    label: string;
    to: string;
  }[];
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const {
    pathname
  } = useLocation();
  const isActive = items.some(item => item.to === pathname);
  useEffect(() => {
    function onDocClick(event: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);
  return <div ref={wrapRef} className="relative" onKeyDown={event => {
    if (event.key === 'Escape') setOpen(false);
  }} data-id="element-318" data-mp="3k2qcn4myvtxi">
      <button type="button" aria-expanded={open} aria-haspopup="true" onClick={() => setOpen(value => !value)} className={`relative flex items-center gap-1 py-2 font-display text-[15px] font-semibold text-brand-navy hover:text-[#083a7c] ${isActive ? 'after:absolute after:-bottom-0.5 after:left-0 after:h-[3px] after:w-full after:rounded-full after:bg-brand-yellow after:content-[""]' : ''}`} data-id="element-319" data-mp="3p1jq4x297kxo">
        Resources
        <ChevronDownIcon aria-hidden="true" className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-320" data-mp="3aehlwdwqxx60" />
      </button>
      {open ? <div className="absolute left-0 top-full z-50 mt-2 w-56 rounded-2xl border-2 border-brand-navy bg-white p-2 shadow-paper" data-id="element-321" data-mp="3jcjjskl6o9p2">
          {items.map(item => <Link key={item.to} to={item.to} onClick={() => setOpen(false)} className="block rounded-xl px-3 py-2.5 font-display text-[15px] font-semibold text-brand-navy hover:bg-brand-tan" data-id="element-322" data-mp="2qnmdzgy5s5d7">
              {item.label}
            </Link>)}
        </div> : null}
    </div>;
}
export function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const closeRef = useRef<HTMLButtonElement>(null);
  const reduceMotion = useReducedMotion();
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);
  useEffect(() => {
    if (!mobileOpen) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setMobileOpen(false);
    }
    document.addEventListener('keydown', onKey);
    closeRef.current?.focus();
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);
  return <header className="sticky top-0 z-40 w-full border-b-2 border-brand-navy bg-brand-tan" data-id="element-323" data-mp="122xebawgvrne">
      <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-4 px-5 py-3 sm:px-8" data-id="element-324" data-mp="1640s7ruyfdlz">
        {/* Official supplied wordmark plus a plain readable descriptor. */}
        <Link to="/" aria-label={`UCF BCM — ${brandDescriptor} — home`} className="flex shrink-0 items-center gap-3 py-1 pr-1" data-id="element-325" data-mp="x3u8ho8nxhed">
          <Wordmark className="h-7 sm:h-8" data-id="element-326" data-mp="3hwxkmv36am7n" />
          <span className="hidden max-w-[16ch] border-l-2 border-brand-navy/25 pl-3 font-meta text-[11px] font-medium leading-tight text-brand-navy md:block lg:max-w-none" data-id="element-327" data-mp="3htjk7se2kn2u">
            {brandDescriptor}
          </span>
        </Link>

        <nav aria-label="Main" className="hidden items-center gap-7 lg:flex" data-id="element-328" data-mp="3ocpma3ppgws9">
          {primaryNav.map(item => item.children ? <ResourcesMenu key={item.label} items={item.children} data-id="element-329" data-mp="3jfz7djpn078f" /> : <NavLink key={item.to} to={item.to} end={item.to === '/'} className={desktopLinkClass} data-id="element-330" data-mp="jyhina7wrzle">
                {item.label}
              </NavLink>)}
          <a href={externalLinks.getConnectedForm} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-[44px] items-center rounded-full border-2 border-brand-navy bg-brand-navy px-5 font-display text-[15px] font-semibold text-white transition-all duration-150 hover:-translate-y-0.5 hover:bg-[#083a7c]" data-id="element-331" data-mp="ax9x7sqhqt8q">
            Get Connected
            <NewTabHint data-id="element-332" data-mp="2gmv2cjoafgls" />
          </a>
        </nav>

        <div className="flex items-center gap-3 lg:hidden" data-id="element-333" data-mp="2lkdh4qozkq1p">
          <a href={externalLinks.getConnectedForm} target="_blank" rel="noopener noreferrer" className="hidden min-h-[44px] items-center rounded-full border-2 border-brand-navy bg-brand-navy px-4 font-display text-sm font-semibold text-white sm:inline-flex" data-id="element-334" data-mp="2lkwojdh0fw1h">
            Get Connected
            <NewTabHint data-id="element-335" data-mp="34auxrwwjnv6p" />
          </a>
          <button type="button" onClick={() => setMobileOpen(true)} aria-expanded={mobileOpen} aria-controls="mobile-menu" className="inline-flex h-12 w-12 items-center justify-center rounded-full border-2 border-brand-navy text-brand-navy transition-colors hover:bg-white" data-id="element-336" data-mp="349tlxq7pf4nx">
            <MenuIcon aria-hidden="true" className="h-6 w-6" data-id="element-337" data-mp="1jv9qth7uilh6" />
            <span className="sr-only" data-id="element-338" data-mp="1jvjgt4sw4smv">Open menu</span>
          </button>
        </div>
      </div>

      <AnimatePresence data-id="element-339" data-mp="1bv3luglsm3vm">
        {mobileOpen ? <motion.div id="mobile-menu" role="dialog" aria-modal="true" aria-label="Site menu" className="fixed inset-0 z-50 flex flex-col bg-brand-tan lg:hidden" initial={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: -12
      }} animate={reduceMotion ? {
        opacity: 1
      } : {
        opacity: 1,
        y: 0
      }} exit={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: -12
      }} transition={{
        duration: reduceMotion ? 0.12 : 0.28,
        ease: [0.22, 0.61, 0.36, 1]
      }} data-id="element-340" data-mp="16w0j52tac9qt">
            <div className="flex items-center justify-between border-b-2 border-brand-navy px-5 py-3" data-id="element-341" data-mp="24ihh8a9uerdm">
              <Link to="/" aria-label="UCF BCM home" className="py-1" data-id="element-342" data-mp="1zm9412f7h893">
                <Wordmark className="h-7" data-id="element-343" data-mp="2dkqomr9te4ms" />
              </Link>
              <button type="button" ref={closeRef} onClick={() => setMobileOpen(false)} className="inline-flex h-12 w-12 items-center justify-center rounded-full border-2 border-brand-navy text-brand-navy" data-id="element-344" data-mp="29ivohq3mdxal">
                <XIcon aria-hidden="true" className="h-6 w-6" data-id="element-345" data-mp="2j5y5p436s41b" />
                <span className="sr-only" data-id="element-346" data-mp="2j5oifo9j0azb">Close menu</span>
              </button>
            </div>
            <nav aria-label="Mobile" className="flex-1 overflow-y-auto px-5 py-6" data-id="element-347" data-mp="2o37v4jqksfp5">
              <p className="font-meta text-[12px] font-medium uppercase tracking-[0.16em] text-brand-navy" data-id="element-348" data-mp="29fd3l6ob0hdn">
                {brandDescriptor}
              </p>
              <ul className="mt-4 space-y-1" data-id="element-349" data-mp="20fq6gx2lxxqe">
                {allRoutes.map(route => <li key={route.to} data-id="element-350" data-mp="24gsb5tfan1zr">
                    <NavLink to={route.to} end={route.to === '/'} className={({
                isActive
              }) => `block rounded-2xl px-4 py-3.5 font-display text-xl font-bold text-brand-navy ${isActive ? 'bg-brand-blue' : 'hover:bg-white'}`} data-id="element-351" data-mp="24hv0vy0vqye3">
                      {route.label}
                    </NavLink>
                  </li>)}
              </ul>
              <a href={externalLinks.getConnectedForm} target="_blank" rel="noopener noreferrer" className="mt-6 flex min-h-[52px] w-full items-center justify-center rounded-full border-2 border-brand-navy bg-brand-navy px-6 font-display text-lg font-semibold text-white" data-id="element-352" data-mp="21zt5tulqkdb0">
                Get Connected
                <NewTabHint data-id="element-353" data-mp="1i9zc3azgno45" />
              </a>
              <p className="mt-4 font-meta text-[13px] text-brand-navy" data-id="element-354" data-mp="1e8nunsqqq971">
                Thursdays at 7:30 PM • UCF Student Union, 3rd Floor
              </p>
            </nav>
          </motion.div> : null}
      </AnimatePresence>
    </header>;
}